import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { useParams, useLocation } from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import io from "socket.io-client";

import { selectRouteStatus, getRoute, skipStop, getCurrentRoutes, getFinisedRoutes, getArchivedRoutes } from "redux/slices/routeSlice";
import { selectUser } from "redux/slices/userSlice";
import { getSupplierLocations, selectSupplierLocations } from "redux/slices/supplierLocationsSlice";
import { ROUTE_CONFIGS } from "config/routeConfigs";
import MapSidebar from "components/Routes/MapSidebar";
import Map from "components/Routes/Map";
import Stop from "components/Routes/Stop";
import Navbar from "components/Navbar";
import DarkLayout from "components/Shared/DarkLayout";
import { setShowMessage } from "redux/slices/uiSlice";
import MenuIcon from "@material-ui/icons/Menu";

const API_URL = import.meta.env.VITE_API.replace("/api/", "");

const useStyles = makeStyles((theme) => ({
  _container: {
    backgroundColor: "#121212",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  _sidebar: {
    maxHeight: "100vh",
    overflow: "scroll",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    zIndex: 1,
  },
  navbarWrapper: {
    "& .MuiAppBar-root": {
      position: "static !important",
    },
  },
}));

const GenericRoutesMap = ({
  routeType,
  selector,
  loadAction,
  features = {}
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { routeId, stopIndex } = useParams();
  const { state: locationState } = useLocation();
  const routes = useSelector(selector);
  const loading = useSelector(selectRouteStatus);
  const user = useSelector(selectUser);
  const allSupplierLocations = useSelector(selectSupplierLocations);

  useEffect(() => {
    dispatch(getSupplierLocations());
  }, [dispatch]);

  const [selected, _setSelected] = useState([]);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [highlighted, setHighlighted] = useState(null);
  const [selectedStop, setSelectedStop] = useState(null);
  const [initialFetch, setInitialFetch] = useState(false);
  const [socket, setSocket] = useState(null);
  const socketRef = useRef(null);
  const [estimated_route, setEstimatedRoute] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const SIDEBAR_EXPANDED = 380;
  const SIDEBAR_COLLAPSED = 60;
  const selectedRef = useRef(selected);
  const setSelected = (s) => {
    selectedRef.current = s;
    _setSelected(s);
  };

  const openStop = useCallback((route_id, stopIndex) => {
    const route = selectedRoutes.find((r) => r.id === route_id);
    if (!route || !Array.isArray(route.pathway)) return;

    let targetIndex = stopIndex;

    const stop = route.pathway[targetIndex];
    if (!stop) return;

    const type = stop.id !== null ? 'customer' : 'supplier';

    setSelectedStop((prev) => {
      if (prev?.route?.id === route_id && prev?.index === targetIndex && prev?.type === type) {
        return prev;
      }
      return {
        route,
        customer: type === 'customer' ? stop : null,
        supplierLocation: type === 'supplier' ? stop : null,
        type,
        index: targetIndex,
      };
    });
  }, [selectedRoutes]);

  const refreshSelected = async () => {
    let i = 0;
    const refreshed = [];
    while (i < selectedRef.current.length) {
      const r = await dispatch(getRoute(selectedRef.current[i]));
      refreshed.push(r);
      i += 1;
    }
    setSelectedRoutes(refreshed);
  };

  const fetchFavourites = async () => {
    setInitialFetch(true);
    let routesThatAreFavourite = [];

    if (routeId) {
      routesThatAreFavourite.push(parseInt(routeId, 10));
    } else {
      let favourites = localStorage.getItem("current-tours-favourites");
      favourites = favourites ? favourites.split(",") : [];
      favourites = favourites.map(Number);
      routesThatAreFavourite = routes.filter((r) => favourites.includes(r.id)).map((r) => r.id);
    }

    let i = 0;
    const initiallySelected = [];

    while (i < routesThatAreFavourite.length) {
      try {
        const full = await dispatch(getRoute(routesThatAreFavourite[i]));
        if (full) {
          initiallySelected.push(full);
        } else {
          dispatch(
            setShowMessage({
              description: "You do not have permissions to view the selected route",
              type: "error",
            })
          );
          routesThatAreFavourite = routesThatAreFavourite.filter((v) => v !== routesThatAreFavourite[i]);
        }
      } catch (err) { }
      i += 1;
    }

    setSelected(routesThatAreFavourite);
    setSelectedRoutes(initiallySelected);
  };

  useEffect(() => {
    if (!initialFetch && (routes.length || routeId)) {
      fetchFavourites();
    }
  }, [routes, initialFetch, routeId]);

  useEffect(() => {
    if (!socket && features.socketUpdates) {
      const newSocket = io(API_URL, {
        query: { token: localStorage.getItem("token") },
      });
      setSocket(newSocket);
      socketRef.current = newSocket;
    }
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (socket && features.socketUpdates) {
      socket.emit("subscribe", { routes: selected });
      const handleRouteUpdated = (data) => {
        refreshSelected();
      };
      const handleRouteStopSkipped = (data) => {
        refreshSelected();
      };
      socket.on("route-updated", handleRouteUpdated);
      socket.on("route-stop-skipped", handleRouteStopSkipped);
      return () => {
        socket.off("route-updated", handleRouteUpdated);
        socket.off("route-stop-skipped", handleRouteStopSkipped);
      };
    }
  }, [socket]);

  useEffect(() => {
    if (socket && features.socketUpdates) {
      socket.emit("subscribe", { routes: selected });
    }
  }, [JSON.stringify(selected)]);

  useEffect(() => {
    if (!loading) {
      if (loadAction === 'current') {
        dispatch(getCurrentRoutes({ skipIfCached: true }));
      } else if (loadAction === 'recent') {
        dispatch(getFinisedRoutes({ skipIfCached: true }));
      } else if (loadAction === 'archived') {
        dispatch(getArchivedRoutes({ skipIfCached: true }));
      }
    }
  }, []);

  const syncedUrlRef = useRef(null);

  useEffect(() => {
    if (routeId === undefined) return;

    const parsedRouteId = parseInt(routeId, 10);
    const parsedStopIndex = parseInt(stopIndex ?? '0', 10);
    if (!Number.isFinite(parsedRouteId) || !Number.isFinite(parsedStopIndex)) return;

    const currentUrlKey = `${parsedRouteId}_${stopIndex ?? 'default'}`;
    if (syncedUrlRef.current === currentUrlKey) return;

    const route = selectedRoutes.find((r) => r.id === parsedRouteId);
    if (!route || !Array.isArray(route.pathway)) return;

    syncedUrlRef.current = currentUrlKey;
    openStop(route.id, parsedStopIndex);
  }, [selectedRoutes, routeId, stopIndex, openStop]);

  useEffect(() => {
    setSelectedStop((prev) => {
      if (!prev) return null;
      const updatedRoute = selectedRoutes.find((r) => r.id === prev.route?.id);
      if (!updatedRoute || !Array.isArray(updatedRoute.pathway) || updatedRoute.isLoading) return prev;

      const targetIndex = prev.index;
      const stop = updatedRoute.pathway[targetIndex];
      if (!stop) return prev;

      const type = stop.id !== null ? 'customer' : 'supplier';
      return {
        route: updatedRoute,
        customer: type === 'customer' ? stop : null,
        supplierLocation: type === 'supplier' ? stop : null,
        type,
        index: targetIndex,
      };
    });
  }, [selectedRoutes]);

  useEffect(() => {
    if (selectedRoutes.length && selectedRoutes[0]?.estimated_route) {
      setEstimatedRoute(selectedRoutes[0]?.estimated_route);
    } else {
      setEstimatedRoute(null);
    }
  }, [selectedRoutes]);

  const onRouteSelect = async (route, addAsFavourite = true) => {
    const full = await dispatch(getRoute(route.id));

    if (addAsFavourite) {
      let favourites = localStorage.getItem("current-tours-favourites");
      favourites = favourites ? favourites.split(",") : [];
      favourites = favourites.concat([route.id]);
      localStorage.setItem("current-tours-favourites", favourites.join(","));
    }

    setSelected([route.id].concat(selected));
    setSelectedRoutes([full].concat(selectedRoutes));
  };

  const onRouteRemove = async (route) => {
    setSelected(selected.filter((id) => id !== route.id));
    setSelectedRoutes(selectedRoutes.filter((r) => r.id !== route.id));

    let favourites = localStorage.getItem("current-tours-favourites");
    favourites = favourites ? favourites.split(",") : [];
    favourites = favourites.filter((f) => parseInt(f, 10) !== route.id);
    localStorage.setItem("current-tours-favourites", favourites.join(","));
  };

  const onSkipStop = async (id, customerId, reason, all = false) => {
    const { index } = selectedStop;
    await dispatch(skipStop(id, customerId, index, reason, all));
    dispatch(
      setShowMessage({
        description: "The stop has been skipped",
        type: "success",
      })
    );
  };

  const goToPreviousStop = () => {
    const { route, index } = selectedStop;
    if (index > 0) {
      openStop(route.id, index - 1);
    }
  };

  const goToNextStop = () => {
    const { route, index } = selectedStop;
    if (index < route.pathway.length - 1) {
      openStop(route.id, index + 1);
    }
  };

  const highlightRoute = useCallback((id) => setHighlighted(id), []);

  React.useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, []);

  const activeSelectedRoutes = useMemo(
    () => selectedRoutes.filter((r) => !r.isLoading),
    [selectedRoutes]
  );

  const closeStop = () => setSelectedStop(null);
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      <div className={classes.navbarWrapper}>
        <Navbar />
      </div>
      <DarkLayout
        pl={2}
        pr={1}
        nopadding
        loading={loading}
        fullHeight
        style={{
          flex: "1 1 auto",
          minHeight: "0 !important",
          height: "100%",
          paddingTop: 0,
          paddingBottom: 0,
        }}
      >
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          <div style={{ position: "absolute", inset: 0 }}>
            <Map
              routes={activeSelectedRoutes}
              highlightRoute={highlightRoute}
              highlightedRouteId={highlighted}
              openStop={openStop}
              user={user}
              selectedStop={selectedStop}
              estimated_route={estimated_route}
              socket={features.socketUpdates ? socket : null}
              initialLat={locationState?.initialLat}
              initialLng={locationState?.initialLng}
              supplierLocations={allSupplierLocations}
            />
          </div>

          <div
            className={classes._sidebar}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: sidebarCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED,
              background: "#121212",
              zIndex: 10,
              transition: "width 0.3s ease",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                flex: "1 1 auto",
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                opacity: sidebarCollapsed ? 0 : 1,
                visibility: sidebarCollapsed ? "hidden" : "visible",
                transition: "opacity 0.15s ease",
              }}
            >
              {!!selectedStop && (
                <Stop
                  route={selectedStop.route}
                  customer={selectedStop.customer}
                  supplierLocation={selectedStop.supplierLocation}
                  type={selectedStop.type}
                  index={selectedStop.index}
                  onClose={closeStop}
                  onSkipStop={features.skipButton ? onSkipStop : null}
                  onPrevStop={goToPreviousStop}
                  onNextStop={goToNextStop}
                  onToggleSidebar={() => setSidebarCollapsed(true)}
                  showSkipButton={features.skipButton}
                />
              )}

              {!selectedStop && (
                <MapSidebar
                  routes={routes.filter((r) => !selected.includes(r.id))}
                  selectedRoutes={selectedRoutes}
                  selectedRoutesIds={selected}
                  onSelect={onRouteSelect}
                  onRemove={onRouteRemove}
                  highlightRoute={highlightRoute}
                  highlightedRouteId={highlighted}
                />
              )}
            </div>
          </div>

          {sidebarCollapsed && (
            <div
              style={{
                position: "absolute",
                top: 7,
                left: 7,
                zIndex: 11,
                background: "#121212",
                borderRadius: 8,
                padding: 6,
                cursor: "pointer",
              }}
              onClick={() => setSidebarCollapsed(false)}
            >
              <MenuIcon style={{ color: "white" }} />
            </div>
          )}
        </div>
      </DarkLayout>
    </div>
  );
};

export default GenericRoutesMap;
